import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type CVStep = {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  data: string;
};

export default function CV() {
  const navigate = useNavigate();
  const [steps, setSteps] = useState<CVStep[]>([
    { id: 'basics', title: 'Basic Info', description: 'Name, phone, location', completed: false, data: '' },
    { id: 'experience', title: 'Experience', description: 'Work history & skills', completed: false, data: '' },
    { id: 'education', title: 'Education', description: 'Schools & training', completed: false, data: '' },
    { id: 'skills', title: 'Skills', description: 'Technical & soft skills', completed: false, data: '' },
    { id: 'review', title: 'Review & Export', description: 'Final check & download', completed: false, data: '' },
  ]);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleCompleteStep = (data: string) => {
    const newSteps = [...steps];
    newSteps[currentStep].data = data;
    newSteps[currentStep].completed = true;
    setSteps(newSteps);
    
    if (currentStep < steps.length - 1) {
      handleNext();
    }
  };
  
  const currentStepData = steps[currentStep];
  
  return (
    <div className="px-4 pt-6 pb-24">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Build Your CV</h1>
        <p className="text-gray-600 mt-1">Step {currentStep + 1} of {steps.length}</p>
      </div>
      
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm text-gray-500">
            {steps.filter(s => s.completed).length}/{steps.length} steps
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-ubuntu-green h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep + 1) / steps.length * 100}%` }}
          ></div>
        </div>
      </div>
      
      {/* Step Content */}
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-2">{currentStepData.title}</h2>
        <p className="text-gray-600 mb-4">{currentStepData.description}</p>
        
        {/* Step-specific content */}
        {currentStep === 0 && <BasicsStep onComplete={handleCompleteStep} />}
        {currentStep === 1 && <ExperienceStep onComplete={handleCompleteStep} />}
        {currentStep === 2 && <EducationStep onComplete={handleCompleteStep} />}
        {currentStep === 3 && <SkillsStep onComplete={handleCompleteStep} />}
        {currentStep === 4 && <ReviewStep />}
      </div>
      
      {/* Navigation */}
      <div className="flex gap-3">
        {currentStep > 0 && (
          <button
            onClick={handlePrev}
            className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-medium active:scale-95 transition-transform"
          >
            Back
          </button>
        )}
        {currentStep < steps.length - 1 && (
          <button
            onClick={handleNext}
            className="flex-1 bg-ubuntu-green text-white py-3 rounded-xl font-medium active:scale-95 transition-transform"
          >
            Next
          </button>
        )}
        {currentStep === steps.length - 1 && (
          <button
            onClick={() => navigate('/')}
            className="flex-1 bg-accent text-white py-3 rounded-xl font-medium active:scale-95 transition-transform"
          >
            Done
          </button>
        )}
      </div>
    </div>
  );
}

// Sub-components for each step
function BasicsStep({ onComplete }: { onComplete: (data: string) => void }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name && phone && location) {
      onComplete(JSON.stringify({ name, phone, location }));
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input"
          placeholder="Enter your full name"
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="input"
          placeholder="e.g. 082 123 4567"
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Town</label>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className="input"
          placeholder="e.g. Senekal, Free State"
          required
        />
      </div>
      
      <button type="submit" className="btn-primary w-full">
        Continue
      </button>
    </form>
  );
}

function ExperienceStep({ onComplete }: { onComplete: (data: string) => void }) {
  return (
    <div className="space-y-4">
      <p className="text-gray-600">Tell us about your work experience:</p>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Most Recent Job</label>
        <input
          type="text"
          className="input"
          placeholder="e.g. Farmer at Green Valley Farms"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
        <select className="input">
          <option value="">Select...</option>
          <option value="1-2">1-2 years</option>
          <option value="3-5">3-5 years</option>
          <option value="5+">5+ years</option>
        </select>
      </div>
      
      <button 
        onClick={() => onComplete('experience-data')} 
        className="btn-primary w-full"
      >
        Continue
      </button>
    </div>
  );
}

function EducationStep({ onComplete }: { onComplete: (data: string) => void }) {
  return (
    <div className="space-y-4">
      <p className="text-gray-600">Tell us about your education:</p>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Highest Qualification</label>
        <select className="input">
          <option value="">Select...</option>
          <option value="none">No formal education</option>
          <option value="primary">Primary school</option>
          <option value="secondary">Secondary school</option>
          <option value="tertiary">Tertiary education</option>
          <option value="vocational">Vocational training</option>
        </select>
      </div>
      
      <button 
        onClick={() => onComplete('education-data')} 
        className="btn-primary w-full"
      >
        Continue
      </button>
    </div>
  );
}

function SkillsStep({ onComplete }: { onComplete: (data: string) => void }) {
  const [skills, setSkills] = useState<string[]>([]);
  const [newSkill, setNewSkill] = useState('');
  
  const addSkill = () => {
    if (newSkill.trim()) {
      setSkills([...skills, newSkill.trim()]);
      setNewSkill('');
    }
  };
  
  const removeSkill = (index: number) => {
    setSkills(skills.filter((_, i) => i !== index));
  };
  
  return (
    <div className="space-y-4">
      <p className="text-gray-600">What skills do you have?</p>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Add a Skill</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            className="input flex-1"
            placeholder="e.g. Farming, Cooking, Driving"
            onKeyDown={(e) => e.key === 'Enter' && addSkill()}
          />
          <button
            onClick={addSkill}
            className="bg-ubuntu-green text-white px-4 rounded-xl"
          >
            +
          </button>
        </div>
      </div>
      
      {skills.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {skills.map((skill, index) => (
            <span
              key={index}
              className="badge bg-ubuntu-green/10 text-ubuntu-green cursor-pointer"
              onClick={() => removeSkill(index)}
            >
              {skill} ×
            </span>
          ))}
        </div>
      )}
      
      <button 
        onClick={() => onComplete(JSON.stringify({ skills }))} 
        className="btn-primary w-full"
      >
        Continue
      </button>
    </div>
  );
}

function ReviewStep() {
  const [isGenerating, setIsGenerating] = useState(false);
  
  const handleGenerate = () => {
    setIsGenerating(true);
    // Simulate CV generation
    setTimeout(() => {
      setIsGenerating(false);
      // Could navigate to CV download or preview
    }, 2000);
  };
  
  return (
    <div className="space-y-4">
      <p className="text-gray-600">Review your information before generating your CV:</p>
      
      <div className="card">
        <h3 className="font-medium mb-2">Basic Info</h3>
        <p className="text-sm text-gray-600">Name: John Doe</p>
        <p className="text-sm text-gray-600">Phone: 082 123 4567</p>
        <p className="text-sm text-gray-600">Town: Senekal</p>
      </div>
      
      <div className="card">
        <h3 className="font-medium mb-2">Experience</h3>
        <p className="text-sm text-gray-600">Farmer at Green Valley Farms (3-5 years)</p>
      </div>
      
      <div className="card">
        <h3 className="font-medium mb-2">Education</h3>
        <p className="text-sm text-gray-600">Secondary school</p>
      </div>
      
      <div className="card">
        <h3 className="font-medium mb-2">Skills</h3>
        <p className="text-sm text-gray-600">Farming, Cooking, Driving</p>
      </div>
      
      <button
        onClick={handleGenerate}
        disabled={isGenerating}
        className="btn-secondary w-full disabled:opacity-50"
      >
        {isGenerating ? 'Generating...' : 'Generate CV'}
      </button>
    </div>
  );
}
